import { api } from 'redux-store/api';
import {
  ITemplateResponse,
  ITemplate,
  ITemplatePhase,
  ITemplatePhaseItems,
  ITemplatePhasePredecessor,
  IGetTemplateByIDBody,
  ITemplateSchduleItemResponse,
  IImportScheduleTemplateCsvPayload,
} from './';

enum Endpoints {
  TEMPLATES = 'schedule-templates/',
}

export const profilesApi = api.injectEndpoints({
  endpoints: (build) => ({
    deleteTemplate: build.mutation<void, number>({
      query: (id) => ({
        url: `${Endpoints.TEMPLATES}${id}`,
        method: 'DELETE',
      }),

      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profilesApi.util.updateQueryData('getTemplatesList', undefined, (draft) => {
            draft.items = draft.items.filter((t) => t.id !== id);
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // rollback on error
          patchResult.undo();
        }
      },
    }),

    getTemplatesList: build.query<{ items: ITemplate[] }, void>({
      query: () => ({
        url: Endpoints.TEMPLATES,
      }),
      providesTags: ['Template'],
    }),

    getTemplateByID: build.query<ITemplate, IGetTemplateByIDBody>({
      query: ({ id }) => ({
        url: `${Endpoints.TEMPLATES}${id}`,
      }),
      providesTags: ['Template'],
    }),

    createTemplate: build.mutation<ITemplateResponse, ITemplate>({
      query: (data) => ({
        url: Endpoints.TEMPLATES,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Template'],
    }),

    cloneTemplate: build.mutation<
      ITemplateResponse,
      Partial<ITemplate & { copyFrom: number | string }>
    >({
      query: (data) => ({
        url: Endpoints.TEMPLATES,
        body: data,
        method: 'POST',
      }),

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;

          dispatch(
            profilesApi.util.updateQueryData('getTemplatesList', undefined, (draft) => {
              draft.items.push({
                ...arg,
                ...result,
              } as ITemplate);
            })
          );
        } catch {}
      },
    }),
    importScheduleTemplateCsv: build.mutation<ITemplateResponse, IImportScheduleTemplateCsvPayload>(
      {
        query: (data) => ({
          url: `${Endpoints.TEMPLATES}import-csv`,
          body: data,
          method: 'POST',
        }),
        invalidatesTags: ['Template'],
      }
    ),

    updateTemplate: build.mutation<
      ITemplateResponse,
      ITemplate & { shouldUpdateQueryData?: boolean }
    >({
      query: ({ id, ...data }) => ({
        url: `${Endpoints.TEMPLATES}${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['Template'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        if (!arg?.shouldUpdateQueryData) return;

        try {
          await queryFulfilled;

          dispatch(
            profilesApi.util.updateQueryData('getTemplatesList', undefined, (draft) => {
              draft.items.forEach((item) => {
                item.isDefault = item.id === arg.id;
              });
            })
          );
        } catch {}
      },
    }),

    createTemplatePhase: build.mutation<ITemplateResponse, ITemplatePhase>({
      query: ({ id, ...data }) => ({
        url: `${Endpoints.TEMPLATES}phases`,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Template'],
    }),

    updateTemplatePhase: build.mutation<ITemplateResponse, ITemplatePhase>({
      query: ({ id, ...data }) => ({
        url: `${Endpoints.TEMPLATES}phases/${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['Template'],
    }),

    deleteTemplatePhase: build.mutation<ITemplateResponse, number>({
      query: (id) => ({
        url: `${Endpoints.TEMPLATES}phases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Template'],
    }),

    createTemplatePhaseItem: build.mutation<ITemplateSchduleItemResponse, ITemplatePhaseItems>({
      query: (data) => ({
        url: `${Endpoints.TEMPLATES}phases-items`,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Template'],
    }),

    updateTemplatePhaseItem: build.mutation<ITemplateResponse, ITemplatePhaseItems>({
      query: ({ id, ...data }) => ({
        url: `${Endpoints.TEMPLATES}phases-items/${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['Template'],
    }),

    deleteTemplatePhaseItem: build.mutation<ITemplateResponse, number>({
      query: (id) => ({
        url: `${Endpoints.TEMPLATES}phases-items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Template'],
    }),

    createTemplatePredecessor: build.mutation<ITemplateResponse, ITemplatePhasePredecessor>({
      query: (data) => ({
        url: `${Endpoints.TEMPLATES}item-predecessors`,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Template'],
    }),

    updateTemplatePredecessor: build.mutation<ITemplateResponse, ITemplatePhasePredecessor>({
      query: ({ itemId, predecessorId, ...data }) => ({
        url: `${Endpoints.TEMPLATES}item-predecessors/${itemId}/${predecessorId}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['Template'],
    }),

    removeTemplatePredecessor: build.mutation<ITemplateResponse, ITemplatePhasePredecessor>({
      query: ({ itemId, predecessorId }) => ({
        url: `${Endpoints.TEMPLATES}item-predecessors/${itemId}/${predecessorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Template'],
    }),
  }),
});

export const {
  useCloneTemplateMutation,
  useDeleteTemplateMutation,
  useImportScheduleTemplateCsvMutation,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useGetTemplateByIDQuery,
  useLazyGetTemplateByIDQuery,
  useCreateTemplatePhaseMutation,
  useUpdateTemplatePhaseMutation,
  useDeleteTemplatePhaseMutation,
  useCreateTemplatePhaseItemMutation,
  useUpdateTemplatePhaseItemMutation,
  useDeleteTemplatePhaseItemMutation,
  useCreateTemplatePredecessorMutation,
  useUpdateTemplatePredecessorMutation,
  useRemoveTemplatePredecessorMutation,
  useGetTemplatesListQuery,
} = profilesApi;
